/**
 * RendererRouter — state machine for XR renderer switching.
 *
 * Tracks whether the engine is in non-XR mode, XR-WebGL mode, or XR-WebGPU mode.
 * Pure state + decision logic; does NOT own renderer instances.
 * App and XRModule handle actual renderer creation/switching.
 */

import { Logger } from '../core/Logger.js';

export type RouterState = 'non-xr' | 'xr-webgl' | 'xr-webgpu';

export class RendererRouter {
  private _state: RouterState = 'non-xr';
  private _binding: 'webgl' | 'webgpu' | null = null;

  get state(): RouterState {
    return this._state;
  }

  get activeBinding(): 'webgl' | 'webgpu' | null {
    return this._binding;
  }

  /**
   * Transition to XR mode with the given binding.
   */
  enterXR(binding: 'webgl' | 'webgpu'): void {
    this._binding = binding;
    this._state = binding === 'webgl' ? 'xr-webgl' : 'xr-webgpu';
    Logger.info('RendererRouter', `Entered XR (${this._state})`);
  }

  /**
   * Transition back to non-XR mode.
   */
  exitXR(): void {
    const prev = this._state;
    this._state = 'non-xr';
    this._binding = null;
    Logger.info('RendererRouter', `Exited XR (was: ${prev})`);
  }

  /**
   * Determine if a renderer switch is needed for the given XR binding.
   *
   * When the primary renderer is WebGPU and we need WebGL XR,
   * a separate forceWebGL renderer is needed. WebGPU XR uses the
   * same pipeline (experimental), so no switch is needed.
   */
  needsRendererSwitch(binding: 'webgl' | 'webgpu', primaryIsWebGPU: boolean): boolean {
    if (binding === 'webgpu') return false; // WebGPU XR uses same pipeline
    return primaryIsWebGPU; // WebGL XR on WebGPU primary needs switch
  }
}
